import React, { useState, useEffect } from 'react';
import { Col, Row, List, Divider, Button, Switch } from 'antd';
import PropTypes from 'prop-types';

import UnlinedTick from 'components/Icons/UnlinedTick';
import DeleteIcon from 'components/Icons/DeleteIcon';
import AddBlockIcon from 'components/Icons/AddBlockIcon';

import './FormList.scss';
import Drawer from 'components/Drawer';
import { PlusCircleIcon } from 'components/Icons';
import FormView from 'pages/Proposal/components/RichEditor/RichEditorModals/FormEditModal/FormView';

const FormList = ({
  formFields,
  chosen,
  onChange,
  configText,
  isRtl,
  modifiedFormTitle,
  setModifiedFormTitle,
  formTitle,
  twoColumns,
  setTwoColumns,
}) => {
  const [filteredFormFields, setFilteredFormFields] = useState(formFields);
  const [optionDrawerVisible, setOptionDrawerVisible] = useState(false);

  useEffect(() => {
    // Mark selected form fields
    const tempFormFields = formFields.map((field) => {
      const isSelected =
        chosen && Array.isArray(chosen) && chosen.some((item) => item.key === field.key);
      return { ...field, selected: isSelected };
    });
    setFilteredFormFields(tempFormFields);
  }, [formFields, chosen]);

  const onDelete = (name) => {
    removeItem(name);
  };

  const addItem = (newItem) => {
    return () => {
      if (newItem) {
        let newChosen = chosen?.length > 0 ? [...chosen] : [];
        const scrollToBottom = () => {
          setTimeout(() => {
            const element = document.querySelector(
              '.formlist-container.tablist-container .tablist-content '
            );
            if (element) {
              element.scrollTo({
                top: element.scrollHeight,
                behavior: 'smooth',
              });
            }
          }, 100);
        };

        if (newItem?.multiple) {
          if (newItem.type === 'checkbox' || newItem.type === 'radio') {
            newChosen.push({
              key:
                newItem.type === 'checkbox'
                  ? configText('Checkboxes header', true)
                  : configText('Radio button header', true),
              value: '',
              editable: true,
              type: newItem.type,
              selected: true,
              saved: true,
              required: false,
              head: false,
              options: [
                {
                  label: configText('option 1', true),
                },
                {
                  label: configText('option 2', true),
                },
              ],
            });
          } else {
            let unq_keys = [...new Set(newChosen.map((item) => item.key))];
            const length = chosen?.length;
            let keyName = `${configText('New Field', true)} ${length}`;
            let j = 1;
            while (unq_keys.includes(keyName)) {
              keyName = `${configText('New Field', true)} ${length + j}`;
              j++;
            }
            newChosen.push({
              key: keyName,
              value: '',
              editable: true,
              type: newItem.type,
              selected: true,
              saved: true,
              required: false,
              head: false,
            });
          }
          scrollToBottom();
        } else {
          // Check if item already exists
          const existingItemIndex = newChosen?.findIndex((item) => item.key === newItem.key);

          if (existingItemIndex !== -1) {
            // Remove item if it exists (toggle behavior)
            newChosen.splice(existingItemIndex, 1);
          } else {
            // Add new item directly in old format
            newChosen.push({
              key: newItem.key,
              type: newItem?.type,
              value: '',
              selected: true,
              saved: true,
              required: false,
              editable: true,
              head: false,
            });
            scrollToBottom();
          }
        }

        onChange(newChosen);
      }
    };
  };

  const removeItem = (name) => {
    const userConfig = JSON.parse(JSON.stringify(window.user?.config || {}));
    let newValue = userConfig.formFields || [];
    newValue = newValue.filter((value) => value.name !== name);
    userConfig.formFields = newValue;
  };

  const renderFormFieldItem = (item) => {
    return (
      <List.Item key={item.key} className={item.selected ? 'selected' : ''} onClick={addItem(item)}>
        <List.Item.Meta title={configText(item.key, true)} />
        {item.editable && (
          <>
            <DeleteIcon
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item.key);
              }}
            />
            <Divider type="vertical" />
          </>
        )}
        {item.multiple && (
          <>
            <AddBlockIcon className="form-editor-add-icon" />
            <Divider type="vertical" />
          </>
        )}

        {item.selected && <UnlinedTick />}
      </List.Item>
    );
  };

  const SidebarList = (
    <>
      <div className="field-heading-container">
        <span>{configText('Select form fields', true)}</span>
        <Row className="two-columns-switch">
          <Switch
            size="small"
            checked={twoColumns}
            onChange={(checked) => setTwoColumns(checked)}
          />{' '}
          <span> Two columns</span>
        </Row>
      </div>
      <List
        dataSource={filteredFormFields}
        renderItem={renderFormFieldItem}
        locale={{
          emptyText: 'No form fields available',
        }}
      />
    </>
  );

  return (
    <Row className="tablist-container formlist-container">
      <div className="ant-modal-mask" />
      <Col className="tablist-prelist-container">
        <Col className="tablist-prelist">
          <div className="field-heading-container">
            <span>{configText('Select form fields', true)}</span>
            <Row className="two-columns-switch">
              <Switch
                size="small"
                checked={twoColumns}
                onChange={(checked) => setTwoColumns(checked)}
              />{' '}
              <span> Two columns</span>
            </Row>
          </div>
          <Row className="tablist-prelist-content">
            <List
              dataSource={filteredFormFields}
              renderItem={renderFormFieldItem}
              locale={{
                emptyText: 'No form fields available',
              }}
            />
          </Row>
        </Col>
      </Col>
      {chosen.length > 0 ? (
        <Col className="tablist-content">
          <div className="tablist-active-tab">
            <div className={`form-fields-preview ${isRtl ? 'language-rtl' : 'language-english'}`}>
              <FormView
                form={chosen && Array.isArray(chosen) ? chosen : []}
                columns={twoColumns ? 2 : 1}
                saveForm={(data) => {
                  onChange(data.formCopy);
                }}
                handleFocus={() => {}}
                setIsDraggable={() => {}}
                configText={configText}
                isRtl={isRtl}
                isFromModal={false}
                modifiedFormTitle={modifiedFormTitle}
                setModifiedFormTitle={setModifiedFormTitle}
                formTitle={formTitle}
              />
              <div className="sidebar-btn below-xl">
                <Button
                  className="large-btn grey-btn"
                  icon={<PlusCircleIcon />}
                  onClick={() => setOptionDrawerVisible(true)}>
                  Add fields
                </Button>
              </div>
            </div>
          </div>
        </Col>
      ) : (
        <Row
          style={{ height: '100%', width: '100%', flexDirection: 'column', gap: '4px' }}
          justify="center"
          align="middle">
          <span className="no-fields-yet desktop-only">No fields yet</span>
          <p className="no-fields-yet-description desktop-only">
            Add fields to your form from the left panel
          </p>
          <div className="sidebar-btn below-xl">
            <Button
              className="large-btn grey-btn"
              icon={<PlusCircleIcon />}
              onClick={() => setOptionDrawerVisible(true)}>
              Add fields
            </Button>
          </div>
        </Row>
      )}
      {optionDrawerVisible && (
        <Drawer
          className="form-option-list-modal"
          visible={optionDrawerVisible}
          setVisible={setOptionDrawerVisible}
          content={SidebarList}
          direction="bottom"
        />
      )}
    </Row>
  );
};

FormList.defaultProps = {
  chosen: [],
  handleState: () => {},
  setSidebarCollapsed: () => {},
};

FormList.propTypes = {
  formFields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      editable: PropTypes.bool,
    })
  ).isRequired,
  chosen: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  handleState: PropTypes.func,
  setSidebarCollapsed: PropTypes.func,
};

export default FormList;
